import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { searchPublicExercises, getRandomPublicExercise, getVideoRanksForExercises } from '../services/exerciseService'
import { getUnlockedHorizonsStyles } from '../services/badgeService'
import { HORIZONS_MUSIC_CATEGORIES, HORIZONS_STYLE_ORDER, getDecadeTag } from '../utils/tagGenerator'
import { getMusicalEra, getMusicalErasForCategory } from '../data/musicalEras'
import { useParcoursImages } from '../contexts/ParcoursImagesContext'
import { getImageUrlAndCrop } from '../data/parcoursIllustrations'
import { getCropBackgroundStyle } from '../utils/cropBackgroundStyle'
import { HORIZONS_FORMATIONS, inferFormationsFromWorkTitle } from '../data/formations'
import { getCodexUrlForEntry } from '../utils/codexContextLinks'
import ExerciseCard from './ExerciseCard'
import './HorizonsMap.css'

const STYLE_GRADIENTS = {
  film: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 50%, #4c1d95 100%)',
  game: 'linear-gradient(135deg, #059669 0%, #047857 50%, #065f46 100%)',
  anime: 'linear-gradient(135deg, #db2777 0%, #be185d 50%, #9d174d 100%)',
  variety: 'linear-gradient(135deg, #ea580c 0%, #c2410c 50%, #9a3412 100%)',
  pop: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 50%, #1e40af 100%)'
}

function HorizonsMap({
  attempts = [],
  userXp = 0,
  onSwitchTab = null,
  onClose = null,
  previewUnlockedHorizonsStyles = null,
  selectedStyleId: controlledStyleId = null,
  setSelectedStyleId: setControlledStyleId = null,
  hideInternalBack = false,
  doneExerciseIds = [],
  publicTeaserOnly = false,
}) {
  const navigate = useNavigate()
  const location = useLocation()
  const { horizonsImageEntries } = useParcoursImages()
  const [internalStyleId, setInternalStyleId] = useState(null)
  /* Contrôle par le parent dès qu'il fournit setSelectedStyleId (même si selectedStyleId est null pour la vue "cartes") */
  const isControlled = setControlledStyleId != null
  const selectedStyleId = isControlled ? controlledStyleId : internalStyleId
  const setSelectedStyleId = isControlled ? setControlledStyleId : setInternalStyleId
  const [styleExercises, setStyleExercises] = useState([])
  const [loadingExercises, setLoadingExercises] = useState(false)
  const [loadingRandom, setLoadingRandom] = useState(false)
  const [videoRanksMap, setVideoRanksMap] = useState(() => new Map())
  const [selectedFormations, setSelectedFormations] = useState([])
  const [selectedDecade, setSelectedDecade] = useState(null)
  const [selectedEra, setSelectedEra] = useState(null)

  const unlockedStyles = useMemo(
    () => (Array.isArray(previewUnlockedHorizonsStyles) && previewUnlockedHorizonsStyles.length > 0
      ? HORIZONS_STYLE_ORDER.filter((id) => previewUnlockedHorizonsStyles.includes(id))
      : getUnlockedHorizonsStyles(attempts, { xp: userXp })),
    [attempts, userXp, previewUnlockedHorizonsStyles]
  )
  const hasAnyUnlocked = unlockedStyles.length > 0

  useEffect(() => {
    if (!selectedStyleId || !hasAnyUnlocked) {
      setStyleExercises([])
      setSelectedFormations([])
      return
    }
    let cancelled = false
    setLoadingExercises(true)
    setSelectedFormations([])
    setSelectedDecade(null)
    setSelectedEra(null)
    searchPublicExercises({
      onlyHorizons: true,
      musicCategory: selectedStyleId,
      ...(publicTeaserOnly ? { publicTeaserOnly: true } : {}),
    })
      .then((list) => {
        if (!cancelled) setStyleExercises(list)
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('Erreur chargement exercices Horizons:', err)
          setStyleExercises([])
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingExercises(false)
      })
    return () => { cancelled = true }
  }, [selectedStyleId, hasAnyUnlocked, publicTeaserOnly])

  useEffect(() => {
    if (!styleExercises.length) {
      setVideoRanksMap(new Map())
      return
    }
    getVideoRanksForExercises(styleExercises).then(setVideoRanksMap).catch(() => setVideoRanksMap(new Map()))
  }, [styleExercises])

  const handleCardClick = (styleId) => {
    if (!unlockedStyles.includes(styleId)) return
    setSelectedStyleId(styleId)
  }

  const handleLaunchRandom = async () => {
    if (!selectedStyleId) return
    setLoadingRandom(true)
    try {
      const filters = {
        onlyHorizons: true,
        musicCategory: selectedStyleId,
        doneExerciseIds,
        ...(publicTeaserOnly ? { publicTeaserOnly: true } : {}),
      }
      if (selectedFormations.length > 0) filters.formation = selectedFormations
      const exercise = await getRandomPublicExercise(filters)
      if (exercise?.id) {
        navigate(`/play/${exercise.id}`, { state: { returnTo: `${location.pathname}${location.search || ''}` } })
      }
    } catch (err) {
      console.error('Erreur lancement exercice aléatoire:', err)
    } finally {
      setLoadingRandom(false)
    }
  }

  if (!hasAnyUnlocked) {
    return (
      <div className="horizons-map">
        <div className="horizons-map-header">
          <h2 className="horizons-map-title">Nouveaux Horizons</h2>
          <p className="horizons-map-subtitle">
            Débloque des styles de musique (film, JV, anime, variété, pop) pour découvrir de nouveaux exercices.
          </p>
        </div>
        <div className="horizons-map-empty">
          <p className="horizons-map-empty-title">Débloque ton premier style</p>
          <p className="horizons-map-empty-text">
            25 exercices complétés, ou série de 7 jours.
          </p>
          {onSwitchTab && (
            <button type="button" className="horizons-map-empty-cta" onClick={() => { onSwitchTab('free-mode'); onClose?.() }}>
              Aller au Mode Libre
            </button>
          )}
        </div>
      </div>
    )
  }

  if (selectedStyleId) {
    const cat = HORIZONS_MUSIC_CATEGORIES[selectedStyleId]
    const formationIdsInList = (() => {
      const ids = new Set()
      styleExercises.forEach(ex => {
        const formationRaw = ex.metadata?.formation
        const arr = Array.isArray(formationRaw) ? formationRaw : (formationRaw ? [formationRaw] : inferFormationsFromWorkTitle(ex.metadata?.workTitle || '', 'horizons'))
        arr.forEach(id => { if (id) ids.add(id) })
      })
      return ids
    })()
    const formationsInList = HORIZONS_FORMATIONS.filter(f => formationIdsInList.has(f.id))
    // Décennies présentes dans la liste
    const decadesInList = (() => {
      const set = new Set()
      styleExercises.forEach(ex => {
        const d = getDecadeTag(ex.metadata?.year)
        if (d) set.add(d)
      })
      return Array.from(set).sort()
    })()
    // Ères musicales présentes dans la liste
    const erasInList = (() => {
      const eraIds = new Set()
      styleExercises.forEach(ex => {
        // Priorité au musicalEra stocké, sinon déduction depuis année
        const eraId = ex.metadata?.musicalEra
        if (eraId) { eraIds.add(eraId); return }
        const era = getMusicalEra(selectedStyleId, ex.metadata?.year)
        if (era) eraIds.add(era.id)
      })
      const allEras = getMusicalErasForCategory(selectedStyleId)
      return allEras.filter(e => eraIds.has(e.id))
    })()
    // Filtrage par formation, décennie ET ère
    const filteredExercises = styleExercises.filter(ex => {
      if (selectedFormations.length > 0) {
        const formationRaw = ex.metadata?.formation
        const exFormations = Array.isArray(formationRaw) ? formationRaw : (formationRaw ? [formationRaw] : inferFormationsFromWorkTitle(ex.metadata?.workTitle || '', 'horizons'))
        if (!selectedFormations.every(s => exFormations.includes(s))) return false
      }
      if (selectedDecade) {
        const d = getDecadeTag(ex.metadata?.year)
        if (d !== selectedDecade) return false
      }
      if (selectedEra) {
        const exEraId = ex.metadata?.musicalEra || getMusicalEra(selectedStyleId, ex.metadata?.year)?.id
        if (exEraId !== selectedEra) return false
      }
      return true
    })
    return (
      <div className="horizons-map">
        <div className={`horizons-map-header horizons-map-header-with-back ${hideInternalBack ? 'horizons-map-header-back-hidden' : ''}`}>
          {!hideInternalBack && (
            <button
              type="button"
              className="horizons-map-back"
              onClick={() => setSelectedStyleId(null)}
              aria-label="Retour aux styles"
            >
              ← Retour
            </button>
          )}
          <h2 className="horizons-map-title">{cat?.label ?? selectedStyleId}</h2>
          <p className="horizons-map-subtitle">
            Choisis un exercice ou lance-en un au hasard.
          </p>
        </div>
        <div className="horizons-map-actions">
          <button
            type="button"
            className="horizons-map-random-btn"
            onClick={handleLaunchRandom}
            disabled={loadingRandom || filteredExercises.length === 0}
          >
            {loadingRandom ? 'Chargement…' : 'Lancer un exercice au hasard'}
          </button>
        </div>
        {formationsInList.length > 0 && (
          <div className="horizons-map-formation-filters">
            <span className="horizons-map-formation-label">Formation (instrumentation) :</span>
            <div className="horizons-map-formation-chips">
              <button
                type="button"
                className={`horizons-map-formation-chip ${selectedFormations.length === 0 ? 'horizons-map-formation-chip-active' : ''}`}
                onClick={() => setSelectedFormations([])}
              >
                Toutes
              </button>
              {formationsInList.map(f => (
                <button
                  key={f.id}
                  type="button"
                  className={`horizons-map-formation-chip ${selectedFormations.includes(f.id) ? 'horizons-map-formation-chip-active' : ''}`}
                  onClick={() => setSelectedFormations(prev => prev.includes(f.id) ? prev.filter(id => id !== f.id) : [...prev, f.id])}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
        )}
        {decadesInList.length > 0 && (
          <div className="horizons-map-formation-filters">
            <span className="horizons-map-formation-label">Décennie :</span>
            <div className="horizons-map-formation-chips">
              <button
                type="button"
                className={`horizons-map-formation-chip ${!selectedDecade ? 'horizons-map-formation-chip-active' : ''}`}
                onClick={() => setSelectedDecade(null)}
              >
                Toutes
              </button>
              {decadesInList.map(d => (
                <button
                  key={d}
                  type="button"
                  className={`horizons-map-formation-chip ${selectedDecade === d ? 'horizons-map-formation-chip-active' : ''}`}
                  onClick={() => setSelectedDecade(prev => prev === d ? null : d)}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>
        )}
        {erasInList.length > 0 && (
          <div className="horizons-map-formation-filters">
            <span className="horizons-map-formation-label">Ère musicale :</span>
            <div className="horizons-map-formation-chips">
              <button
                type="button"
                className={`horizons-map-formation-chip ${!selectedEra ? 'horizons-map-formation-chip-active' : ''}`}
                onClick={() => setSelectedEra(null)}
              >
                Toutes
              </button>
              {erasInList.map(era => (
                <button
                  key={era.id}
                  type="button"
                  className={`horizons-map-formation-chip ${selectedEra === era.id ? 'horizons-map-formation-chip-active' : ''}`}
                  onClick={() => setSelectedEra(prev => prev === era.id ? null : era.id)}
                >
                  {era.label}
                </button>
              ))}
            </div>
          </div>
        )}
        {loadingExercises ? (
          <div className="horizons-map-loading">
            <div className="spinner-small" />
          </div>
        ) : filteredExercises.length === 0 ? (
          <div className="horizons-map-empty horizons-map-empty-inline">
            <p>{(selectedFormations.length > 0 || selectedDecade || selectedEra) ? 'Aucun exercice pour ces filtres.' : 'Aucun exercice dans ce style pour l\u0027instant.'}</p>
          </div>
        ) : (
          <div className="horizons-map-exercise-list">
            {filteredExercises.map((ex) => {
              const vr = videoRanksMap.get(ex.id)
              return (
              <ExerciseCard
                key={ex.id}
                exercise={ex}
                allExercises={filteredExercises}
                videoRank={vr?.rank}
                videoGroupSize={vr?.groupSize}
                showDemoTeaserBadge={publicTeaserOnly}
                onClick={(id) => navigate(`/play/${id}`, { state: { returnTo: `${location.pathname}${location.search || ''}` } })}
                onOpenCodex={(ficheId, sectionId) => navigate(getCodexUrlForEntry(ficheId, sectionId))}
              />
            )})}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="horizons-map">
      <div className="horizons-map-header">
        <h2 className="horizons-map-title">Nouveaux Horizons</h2>
        <p className="horizons-map-subtitle">
          Choisis un style pour voir les exercices disponibles.
        </p>
      </div>
      <div className="horizons-map-cards">
        {HORIZONS_STYLE_ORDER.map((styleId) => {
          const unlocked = unlockedStyles.includes(styleId)
          const cat = HORIZONS_MUSIC_CATEGORIES[styleId]
          const rawEntry = horizonsImageEntries[styleId]
          const { url: imageUrl, crop: imageCrop } = getImageUrlAndCrop(rawEntry)
          const gradient = STYLE_GRADIENTS[styleId] || STYLE_GRADIENTS.film
          const cardStyle = imageUrl
            ? {
                backgroundImage: `linear-gradient(135deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.05) 60%), url(${imageUrl})`,
                ...(imageCrop
                  ? getCropBackgroundStyle(imageCrop, 1.6, 8 / 5)
                  : { backgroundSize: 'cover', backgroundPosition: 'center' }),
              }
            : { background: gradient }
          return (
            <button
              key={styleId}
              type="button"
              className={`horizons-style-card ${unlocked ? 'horizons-style-card-unlocked' : 'horizons-style-card-locked'}`}
              style={cardStyle}
              onClick={() => handleCardClick(styleId)}
              disabled={!unlocked}
            >
              <span className="horizons-style-card-inner">
                <span className="horizons-style-card-label">{cat?.tagLabel ?? styleId}</span>
                <span className="horizons-style-card-subtitle">{cat?.label ?? styleId}</span>
                {!unlocked && (
                  <span className="horizons-style-card-lock" aria-hidden>🔒</span>
                )}
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

export default HorizonsMap
